import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShoppingCart, Trash2, X, Ticket, Heart, Award, CreditCard, Banknote } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

export default function CartButton() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (user) fetchCart();
  }, [user, open]);

  const fetchCart = async () => {
    try {
      const res = await axios.get(`${API}/cart`, { withCredentials: true });
      setCart(res.data || { items: [], total: 0 });
    } catch {}
  };

  const removeItem = async (itemId) => {
    try {
      await axios.delete(`${API}/cart/${itemId}`, { withCredentials: true });
      fetchCart();
    } catch { toast.error("Failed to remove"); }
  };

  const checkout = async (paymentMethod) => {
    setChecking(true);
    try {
      if (paymentMethod === "online") {
        // Stripe checkout
        const res = await axios.post(`${API}/payments/checkout`, {
          origin_url: window.location.origin,
          order_type: "cart"
        }, { withCredentials: true });
        if (res.data.url) {
          window.location.href = res.data.url;
          return;
        }
      }
      // Cash checkout
      const res = await axios.post(`${API}/cart/checkout`, { payment_method: paymentMethod }, { withCredentials: true });
      toast.success("Order confirmed! Tickets added to your wallet.");
      setCart({ items: [], total: 0 });
      setOpen(false);
      navigate("/dashboard");
    } catch (err) { toast.error(err.response?.data?.detail || "Checkout failed"); }
    finally { setChecking(false); }
  };

  if (!user) return null;

  const itemCount = cart.items?.length || 0;
  const iconMap = { ticket: Ticket, donation: Heart, sponsorship: Award };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="relative p-2 text-slate-600 hover:text-deep-blue transition-colors" data-testid="cart-button">
          <ShoppingCart className="w-5 h-5" />
          {itemCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{itemCount}</span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><ShoppingCart className="w-5 h-5" /> Shopping Cart</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {cart.items?.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Your cart is empty</p>
              <Link to="/events" onClick={() => setOpen(false)}>
                <Button variant="outline" className="mt-4">Browse Events</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {cart.items.map(item => {
                const Icon = iconMap[item.item_type] || Ticket;
                return (
                  <div key={item.item_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`cart-item-${item.item_id}`}>
                    <div className="w-10 h-10 bg-deep-blue/10 rounded-lg flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-deep-blue" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-deep-blue truncate">{item.event_title || item.sponsorship_level || item.description || "Item"}</h4>
                      <p className="text-xs text-slate-500">{item.item_type === "ticket" ? `${item.quantity}x ${item.ticket_type_name}` : item.item_type}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-deep-blue">${(item.price * item.quantity).toFixed(2)}</p>
                      <button onClick={() => removeItem(item.item_id)} className="text-red-400 hover:text-red-600 mt-1"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {cart.items?.length > 0 && (
          <div className="border-t pt-4 space-y-3">
            <div className="flex items-center justify-between text-lg font-bold text-deep-blue">
              <span>Total</span>
              <span>${cart.total?.toFixed(2)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={() => checkout("cash")} disabled={checking} className="bg-amber-600 text-white hover:bg-amber-700 flex items-center gap-2" data-testid="checkout-cash">
                <Banknote className="w-4 h-4" /> Pay at Event
              </Button>
              <Button onClick={() => checkout("online")} disabled={checking} className="bg-deep-blue text-white flex items-center gap-2" data-testid="checkout-online">
                <CreditCard className="w-4 h-4" /> Pay Online
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 text-center">Online payments will be available when Stripe is configured</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
